import React, { useEffect, useState } from 'react'
import { Link, withRouter } from 'react-router-dom'
import IconBack from '@material-ui/icons/ArrowBack'
import { injectIntl } from 'react-intl'
import { Switch } from '@material-ui/core'
import { connect } from 'react-redux'
import Button from '@material-ui/core/Button'
import { Field, Form } from 'react-final-form'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import {
  updateNotificationSettings,
  updateProfile,
} from 'reducers/profile/reducer'
import withPromise from 'helpers/withPromise'
import { Checkbox } from 'components/common/FormInputs'
import { ReactComponent as MenuIconBell } from 'assets/img/menu/menuIconBell.svg'
import { ReactComponent as MenuIconProfile } from 'assets/img/menu/menuIconProfile.svg'
import * as ROUTE from 'constants/appRouting'
import messages from './translate'

function ProfileNotifications({
  intl: { formatMessage },
  history: { goBack },
  updateProfile,
  updateNotificationSettings,
  userData,
}) {
  const data = userData.toJS()
  const {
    notificationsEnabled,
    name,
    locale,
    email,
    notificationSettings,
  } = data
  const [isUpdating, setIsUpdating] = useState(false)
  const indexedNotificationSettings = notificationSettings.map(
    (item, index) => ({ ...item, index }),
  )
  const jobsNotificationSettings = indexedNotificationSettings.filter(
    ({ category }) => category === 'jobs',
  )

  useEffect(() => {
    setIsUpdating(false)
  }, [notificationsEnabled])

  const onEnableNotifications = () => {
    const payloadData = {
      email,
      name,
      locale,
      notifications_enabled: !notificationsEnabled,
    }

    setIsUpdating(true)

    return withPromise(updateProfile, {
      data: payloadData,
      successMessage: formatMessage(messages.profileWasUpdated),
    }).then(errors => Promise.resolve(errors))
  }

  const onSaveInfo = ({ notificationSettings }) => {
    const formattedNotificationSettings = notificationSettings.map(item => ({
      ...item,
      category: undefined,
    }))

    return withPromise(updateNotificationSettings, {
      data: formattedNotificationSettings,
      successMessage: formatMessage(messages.profileWasUpdated),
    }).then(errors => Promise.resolve(errors))
  }

  const renderSectionRow = ({ name, index }, submitting) => (
    <tr className="table-row" key={name}>
      <td className="cell name-cell">{formatMessage(messages[name])}</td>
      <td className="cell email-cell">
        <FormControlLabel
          disabled={submitting}
          control={
            <Field
              name={`notificationSettings[${index}].emailEnabled`}
              component={Checkbox}
              type="checkbox"
              color="primary"
            />
          }
        />
      </td>
      <td className="cell sms-cell">
        <FormControlLabel
          disabled={submitting}
          control={
            <Field
              name={`notificationSettings[${index}].smsEnabled`}
              component={Checkbox}
              type="checkbox"
              color="primary"
            />
          }
        />
      </td>
    </tr>
  )

  const renderTableSection = (section, sectionName, submitting) => (
    <>
      <tr className="table-row table-head">
        <td className="cell name-cell">{sectionName}</td>
        <td className="cell email-cell">{formatMessage(messages.email)}</td>
        <td className="cell sms-cell">{formatMessage(messages.sms)}</td>
      </tr>
      {section.map(row => renderSectionRow(row, submitting))}
    </>
  )

  return (
    <div className="profile-page">
      <aside className="profile-page-sidebar">
        <div className="profile-page-title">
          <IconBack
            onClick={() => {
              goBack()
            }}
          />
          {formatMessage(messages.settings)}
        </div>
        <nav className="profile-page-nav">
          <div className="nav-block">
            <div className="nav-block-title">
              {formatMessage(messages.personalSettings)}
            </div>
            <Link to={ROUTE.PROFILE_PERSONAL_INFO}>
              <MenuIconProfile />
              {formatMessage(messages.personalInfo)}
            </Link>
            <Link to={ROUTE.PROFILE_NOTIFICATIONS} className="active">
              <MenuIconBell />
              {formatMessage(messages.myNotifications)}
            </Link>
          </div>
        </nav>
      </aside>
      <div className="profile-page-content">
        <h2 className="profile-page-title">
          <MenuIconBell />
          {formatMessage(messages.myNotifications)}
        </h2>
        <Form
          onSubmit={onSaveInfo}
          initialValues={data}
          render={({ handleSubmit, submitting, pristine }) => (
            <div className="profile-page-edit-section-holder">
              <div className="profile-page-edit-section">
                <section className="profile-page-edit-section-block">
                  <div className="profile-page-title-section">
                    <div className="title">
                      {formatMessage(messages.allowNotifications)}
                    </div>
                    <Switch
                      href={null}
                      color="primary"
                      checked={notificationsEnabled}
                      onClick={onEnableNotifications}
                      disabled={isUpdating}
                    />
                  </div>
                </section>
                <section className="profile-page-edit-section-block">
                  <div className="profile-page-title-section">
                    <div className="title">
                      {formatMessage(messages.notificationPreferences)}
                    </div>
                  </div>
                  <table className="profile-page-table-section">
                    <tbody>
                      {!!jobsNotificationSettings.length &&
                        renderTableSection(
                          jobsNotificationSettings,
                          formatMessage(messages.jobs),
                          submitting,
                        )}
                    </tbody>
                  </table>
                </section>
                <div className="submit-form-block">
                  <Button
                    disabled={submitting || pristine}
                    className="profile-page-button primary"
                    onClick={handleSubmit}>
                    {formatMessage(messages.save)}
                  </Button>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  )
}

export default connect(
  state => ({
    userData: state.getIn(['profile', 'profile']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
  }),
  {
    updateProfile,
    updateNotificationSettings,
  },
)(withRouter(injectIntl(ProfileNotifications)))
